import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Calendar,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Users,
  TrendingUp
} from 'lucide-react';
import { getWeeklySessions } from '../../lib/airtable';
// Uses shared facilitator colors utility to ensure consistent styling across components
import { getFacilitatorColor, facilitatorColors } from '../../lib/facilitatorColors';
import SessionsCalendar from '../admin/SessionsCalendar';

// Cache key for sales dashboard data (separate from admin cache)
const SALES_DASHBOARD_CACHE_KEY = 'sales_dashboard_sessions';
const SALES_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const SalesHome = () => {
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todaySessionsLoaded, setTodaySessionsLoaded] = useState(false);
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [upcomingExpanded, setUpcomingExpanded] = useState(true);
  
  // Ref to track loading state for timeout handler (prevents race condition)
  const loadingRef = useRef(true);
  const timeoutRef = useRef(null);

  // Sync loading state with ref to prevent race conditions in timeout
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      loadingRef.current = false;
    };
  }, []);

  useEffect(() => {
    async function loadDashboardData() {
      // Check for cached dashboard data first
      try {
        const cached = sessionStorage.getItem(SALES_DASHBOARD_CACHE_KEY);
        if (cached) {
          const { timestamp, data } = JSON.parse(cached);
          
          // Check if cache is still valid (10 minutes)
          if (Date.now() - timestamp < SALES_CACHE_TTL) {
            setAllSessions(data);
            setTodaySessionsLoaded(true);
            setLoading(false);
            return; // Skip API fetch entirely
          } else {
            sessionStorage.removeItem(SALES_DASHBOARD_CACHE_KEY);
          }
        }
      } catch (error) {
        console.warn('Error reading sales dashboard cache:', error);
        // Clear potentially corrupted cache
        try {
          sessionStorage.removeItem(SALES_DASHBOARD_CACHE_KEY);
        } catch (clearError) {
          console.warn('Could not clear corrupted sales cache:', clearError);
        }
      }

      // Performance monitoring - track sales dashboard load time
      const startTime = performance.now();

      // Set a timeout to ensure UI doesn't hang indefinitely
      timeoutRef.current = setTimeout(() => {
        if (loadingRef.current) {
          console.warn('⚠️ Dashboard load timeout after 10 seconds');
          setError('Dashboard load timeout - please refresh');
          setLoading(false);
          loadingRef.current = false;
        }
      }, 10000); // 10 second timeout
      
      try {
        setLoading(true);
        loadingRef.current = true;
        const weeklyData = await getWeeklySessions();
        
        // Clear timeout since we got data
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        const loadTime = performance.now() - startTime;

        // Send performance metrics to monitoring (GA4/Sentry)
        if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
          window.gtag('event', 'sales_dashboard_load', {
            event_category: 'performance',
            value: Math.round(loadTime),
            session_count: weeklyData.length,
            custom_parameter_1: loadTime < 2000 ? 'fast' : 'slow'
          });
        }

        // Warn if load time is too slow (> 2 seconds target)
        if (loadTime > 2000) {
          console.warn(`⚠️ Sales dashboard slow load: ${loadTime.toFixed(2)}ms (target: <2000ms)`);
          if (typeof window !== 'undefined' && typeof window.Sentry !== 'undefined' && window.Sentry.captureMessage) {
            window.Sentry.captureMessage('Slow sales dashboard load', {
              level: 'warning',
              extra: { 
                loadTime: loadTime.toFixed(2), 
                sessionCount: weeklyData.length,
                target: '2000ms'
              }
            });
          }
        }
        
        setAllSessions(weeklyData);
        setTodaySessionsLoaded(true);

        // Cache the dashboard data for future visits
        try {
          const cacheData = {
            timestamp: Date.now(),
            data: weeklyData
          };
          sessionStorage.setItem(SALES_DASHBOARD_CACHE_KEY, JSON.stringify(cacheData));
          console.log(`✅ Cached ${weeklyData.length} sales dashboard sessions for 10 minutes`);
        } catch (cacheError) {
          console.warn('Failed to cache sales dashboard data:', cacheError);
        }
      } catch (err) {
        // Clear timeout on error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        const loadTime = performance.now() - startTime;
        console.error(`❌ SalesHome: Dashboard load failed after ${loadTime.toFixed(2)}ms`, err);
        
        // Track errors in monitoring
        if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
          window.gtag('event', 'sales_dashboard_error', {
            event_category: 'error',
            error_message: err.message || 'Unknown error'
          });
        }
        
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    }

    loadDashboardData();
  }, []);

  // Categorize sessions by time period
  const todaySessions = allSessions
    .filter(session => session.isToday)
    .sort((a, b) => {
      // Sort by date, oldest first (ascending order)
      const dateA = new Date(a.sessionDate || a.dateTime);
      const dateB = new Date(b.sessionDate || b.dateTime);
      return dateA - dateB;
    });
  const upcomingSessions = allSessions
    .filter(session => session.isUpcoming && !session.isToday)
    .sort((a, b) => {
      // Sort by date, oldest first (ascending order)
      const dateA = new Date(a.sessionDate || a.dateTime);
      const dateB = new Date(b.sessionDate || b.dateTime);
      return dateA - dateB;
    });
  const completedSessions = allSessions
    .filter(session => session.isPast)
    .sort((a, b) => {
      // Sort by date, oldest first (ascending order)
      const dateA = new Date(a.sessionDate || a.dateTime);
      const dateB = new Date(b.sessionDate || b.dateTime);
      return dateA - dateB;
    });



  // Helper function to render multiple facilitators
  const renderFacilitators = (session, maxVisible = 2) => {
    const facilitators = session.facilitators && session.facilitators.length > 0 
      ? session.facilitators 
      : [{ 
          id: session.facilitatorId, 
          name: session.facilitatorName || 'Unassigned', 
          color: session.facilitatorColor || 'gray' 
        }];

    const visibleFacilitators = facilitators.slice(0, maxVisible);
    const remainingCount = Math.max(0, facilitators.length - maxVisible);

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visibleFacilitators.map((facilitator, index) => {
          const colors = facilitatorColors[facilitator.color] || facilitatorColors.gray;
          return (
            <span 
              key={facilitator.id || index}
              className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
              title={facilitator.name}
            >
              {facilitator.name}
            </span>
          );
        })}
        {remainingCount > 0 && (
          <span 
            className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200"
            title={`+${remainingCount} more: ${facilitators.slice(maxVisible).map(f => f.name).join(', ')}`}
          >
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  // Render a session list
  const renderSessionList = (sessions, showAsCards = false) => {
    if (sessions.length === 0) {
      return (
        <div className="text-center py-4">
          <Calendar className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No sessions found</p>
        </div>
      );
    }

    if (showAsCards) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => {
            const facilitatorColor = getFacilitatorColor(session);
            return (
            <Card key={session.id} className={`hover:shadow-md transition-shadow border ${facilitatorColor.border}`}>
              <CardContent className={`p-4 ${facilitatorColor.bg}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="font-medium text-gray-900 text-sm truncate pr-2">
                        {session.sessionName}
                      </h4>
                      <div className="flex items-center space-x-1 text-xs text-gray-500 flex-shrink-0">
                        <Users className="h-3 w-3" />
                        <span>{session.expectedAttendees}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      {session.programName} • SO: {session.soNumber}
                    </p>
                    <div className="mb-2">
                      {renderFacilitators(session, 1)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {session.dateTime}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/sales/scheduled-offerings/search?so=${session.soNumber}`}>
                      View
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {sessions.map((session) => {
          const facilitatorColor = getFacilitatorColor(session);
          return (
          <div key={session.id} className={`py-3 px-3 rounded-lg border ${facilitatorColor.border} ${facilitatorColor.bg} relative pb-12 sm:pb-3`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate pr-2">
                  {session.sessionName}
                </h4>
              </div>
              <div className="flex items-center space-x-1 text-xs text-gray-500 flex-shrink-0">
                <Users className="h-3 w-3" />
                <span>{session.expectedAttendees}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {session.dateTime}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {session.programName} • SO: {session.soNumber}
            </p>
            <div className="mt-1">
              {renderFacilitators(session, 2)}
            </div>
            <div className="absolute bottom-3 right-3 left-3 sm:left-auto">
              <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
                <Link to={`/sales/scheduled-offerings/search?so=${session.soNumber}`}>
                  View SO
                </Link>
              </Button>
            </div>
          </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 flex items-center">
          <TrendingUp className="h-8 w-8 text-blue-600 mr-3" />
          Sales Dashboard
        </h2>
        {loading && todaySessionsLoaded && (
          <div className="text-sm text-blue-600 mt-2">
            ⏳ Loading additional sessions in background...
          </div>
        )}
      </div>


      {/* Sessions Overview */}
      <div className="space-y-6">
        {/* Today's Sessions */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Today&apos;s Sessions</h3>
          {loading && !todaySessionsLoaded ? (
            <div className="space-y-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <Skeleton className="h-4 w-48 mb-2" />
                        <Skeleton className="h-3 w-32 mb-2" />
                        <Skeleton className="h-3 w-40" />
                      </div>
                      <div className="flex items-center space-x-1">
                        <Skeleton className="h-3 w-3 rounded-full" />
                        <Skeleton className="h-3 w-6" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-6 w-20 rounded-full" />
                      <Skeleton className="h-8 w-16" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card>
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-600 text-sm">Failed to load today&apos;s sessions</p>
              </CardContent>
            </Card>
          ) : (
            renderSessionList(todaySessions, true)
          )}
        </div>

        {/* Upcoming Sessions - Collapsible */}
        <div>
          <div 
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => setUpcomingExpanded(!upcomingExpanded)}
          >
            <div className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Upcoming This Week ({upcomingSessions.length})</h3>
            </div>
            {upcomingExpanded ? (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            )}
          </div>
          
          {upcomingExpanded && (
            <Card>
              <CardContent className="p-6">
                {loading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="py-3 px-3 rounded-lg border border-gray-100 relative pb-12 sm:pb-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <Skeleton className="h-4 w-48 mb-2" />
                            <Skeleton className="h-3 w-32 mb-1" />
                            <Skeleton className="h-3 w-40 mb-1" />
                          </div>
                          <div className="flex items-center space-x-1">
                            <Skeleton className="h-3 w-3 rounded-full" />
                            <Skeleton className="h-3 w-6" />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Skeleton className="h-6 w-20 rounded-full" />
                          <Skeleton className="h-8 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                    <p className="text-red-600 text-sm">Failed to load upcoming sessions</p>
                  </div>
                ) : (
                  renderSessionList(upcomingSessions)
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Completed Sessions - Collapsible */}
        <div>
          <div 
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => setCompletedExpanded(!completedExpanded)}
          >
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Completed This Week ({completedSessions.length})</h3>
            </div>
            {completedExpanded ? (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            )}
          </div>
          
          {completedExpanded && (
            <Card>
              <CardContent className="p-6">
                {loading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="py-3 px-3 rounded-lg border border-gray-100 relative pb-12 sm:pb-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <Skeleton className="h-4 w-48 mb-2" />
                            <Skeleton className="h-3 w-32 mb-1" />
                            <Skeleton className="h-3 w-40 mb-1" />
                          </div>
                          <div className="flex items-center space-x-1">
                            <Skeleton className="h-3 w-3 rounded-full" />
                            <Skeleton className="h-3 w-6" />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Skeleton className="h-6 w-20 rounded-full" />
                          <Skeleton className="h-8 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                    <p className="text-red-600 text-sm">Failed to load completed sessions</p>
                  </div>
                ) : (
                  renderSessionList(completedSessions)
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Calendar View - Collapsible */}
      <div>
        <div 
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => setCalendarExpanded(!calendarExpanded)}
        >
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Sessions Calendar</h3>
          </div>
          {calendarExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-500" />
          )}
        </div>
        
        {calendarExpanded && (
          <SessionsCalendar sessions={allSessions} loading={loading} />
        )}
      </div>

    </div>
  );
};

export default SalesHome;